import { InstantProducts, SearchBox } from '@coveo/headless-react/ssr-commerce';
import { FindHome, PageSection, PageSectionContent } from '@greystar/designsystem';
import {
  extractSearchBoxResults,
  SearchBoxResult,
  setFocus,
  handleSearchKeyDown,
  useDropdownFocusHandler,
  useControllerSubscription,
  createSearchInputHandler,
  useSearchResultClickHandler,
  useSearchSuggestions,
  createInputFocusHandler,
  handleInputClick,
  useSearchAnalytics,
  createInputChangeHandler,
  buildCommonInputProps,
  handleDropdownFocusWithButton,
  getDictionaryEntry,
} from '@greystar/lib';
import React, { useEffect, useState, useId } from 'react';
import { SearchBoxCsr } from '../search/search-box/search-box-csr';
import { useI18n } from 'next-localization';

export interface HomeHeroProps {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  description?: React.ReactNode;
  imageSrc: React.ReactElement;
  placeholderText: string;
  controllerSearchBox: SearchBox;
  controllerInstantProducts: InstantProducts;
  internalSearchPage: boolean;
  state?: string;
  propertyLocation?: string;
  propertyId?: string;
  propertyName?: string;
  backgroundColor?: string;
  secondarySubtitle?: React.ReactNode;
}

export function HomeHero({
  title,
  subtitle,
  imageSrc,
  placeholderText,
  description,
  controllerSearchBox,
  internalSearchPage,
  controllerInstantProducts,
  backgroundColor,
  secondarySubtitle,
}: Readonly<HomeHeroProps>) {
  const [stateSearchBox, setStateSearchBox] = useState(controllerSearchBox.state);
  const [stateInstantProducts, setStateInstantProducts] = useState(controllerInstantProducts.state);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [modalInputValue, setModalInputValue] = useState('');
  const [items, setItems] = useState<SearchBoxResult[]>([]);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const { pushDataLayerEvent } = useSearchAnalytics();
  const id = useId();
  const { t } = useI18n();
  const searchInputID = 'home-search-' + id;
  const [inputIcon, setInputIcon] = useState<'Search' | 'CloseAlt' | undefined>('Search');

  const args = {
    isFocussed: isInputFocused,
  };

  useSearchSuggestions({
    searchBoxState: stateSearchBox,
    controllerInstantProducts,
    setItems,
  });

  // Handle Input box click / focus
  const handleInputFocus = createInputFocusHandler({
    setIsInputFocused,
    setInputIcon,
    inputId: searchInputID,
  });

  // Search result click handler
  useSearchResultClickHandler(searchInputValue);

  // Check click outside dropdown
  useDropdownFocusHandler(
    setIsInputFocused,
    setInputIcon,
    setSearchInputValue,
    setModalInputValue,
    setItems,
    false,
    searchInputID
  );
  useControllerSubscription(controllerSearchBox, setStateSearchBox);
  useControllerSubscription(controllerInstantProducts, setStateInstantProducts);

  // Handle search input box changes and fetch result
  const onSearchBoxInputChange = createSearchInputHandler({
    controllerSearchBox,
    controllerInstantProducts,
    setItems,
    minQueryLength: 2,
  });

  // Handle search button click on search input box
  const onDropdownFocus = () => {
    handleDropdownFocusWithButton({
      setIsInputFocused,
      setInputIcon,
      pushDataLayerEvent,
      setSearchInputValue,
      setItems,
      inputIcon,
      setModalInputValue,
      uniqueId: searchInputID,
    });
  };

  // Update any references to homeItems -> items
  useEffect(() => {
    setItems([]);

    if (searchInputValue.length > 2) {
      setItems(extractSearchBoxResults(stateInstantProducts.products, stateInstantProducts.query));
    }
  }, [stateInstantProducts]);

  // Handle input box click event
  const onInputClick = () => {
    handleInputClick(pushDataLayerEvent);
  };

  // Handle Input box change to update search icon and get data from coveo
  const handleInputChange = createInputChangeHandler({
    setSearchInputValue,
    setInputIcon,
    onSearchBoxInputChange,
    setIsInputFocused,
  });

  // Search input box events
  const commonInputProps = buildCommonInputProps({
    value: searchInputValue ?? modalInputValue,
    onFocus: handleInputFocus,
    onChange: handleInputChange,
    onClick: onInputClick,
    onKeyDown: handleSearchKeyDown(items),
    ariaLabel: getDictionaryEntry(
      t('search-input-aria-label'),
      placeholderText ?? 'Search by city or Greystar Community'
    ),
  });

  const handleModalClick = () => {
    setIsModalOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isModalOpen && typeof setFocus === 'function') {
      setFocus('modal');
    }
  }, [isModalOpen]);

  const searchDropdownProps = {
    isFocussed: isInputFocused,
    id: searchInputID,
    modalTrigger: {
      onClick: handleModalClick,
      label: placeholderText,
    },
    results: {
      labelProps: {
        label: placeholderText,
      },
      items: items,
    },
    input: {
      inputProps: {
        placeholder: isInputFocused ? '' : placeholderText,
        ...commonInputProps,
      },
      button: { onClick: onDropdownFocus },
      iconName: inputIcon,
    },
  };

  // This is the simplest and most direct way to resolve the React 19 stricter type safety issue.
  // It forces TypeScript to accept the `image` prop.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const typedImage = imageSrc as any;

  return (
    <PageSection
      topColour={internalSearchPage ? null : 'sky'}
      bottomColour={internalSearchPage ? backgroundColor : null}
      isFlushed
    >
      <PageSectionContent>
        <FindHome
          {...args}
          title={title}
          subtitle={subtitle}
          description={description}
          isInternalSearch={internalSearchPage}
          searchDropdown={searchDropdownProps}
          image={typedImage}
          secondarySubtitle={secondarySubtitle}
        />
        {isModalOpen && (
          <SearchBoxCsr
            variant="modal"
            controllerSearchBox={controllerSearchBox}
            controllerInstantProducts={controllerInstantProducts}
            headerTitle={'Search for homes'}
            headerSubtitle={placeholderText}
            onClose={handleModalClick}
            searchPromptText={'Search for homes'}
            searchResultsTitle=""
          />
        )}
      </PageSectionContent>
    </PageSection>
  );
}
